import { useEffect, useState } from "react";
import axios from "axios";
import { motion, AnimatePresence } from "framer-motion";
import { X, ChevronLeft, ChevronRight } from "lucide-react";
import PageHero from "@/components/site/PageHero";
import { usePageMeta } from "@/components/site/usePageMeta";
import { useLang } from "@/i18n/LangContext";
import { GALLERY } from "@/data/gallery";
import { API, imgUrl } from "@/lib/api";

const Lightbox = ({ items, index, setIndex }) => {
  const item = items[index];
  const go = (d) => setIndex((index + d + items.length) % items.length);
  return (
    <motion.div className="fixed inset-0 z-[80] bg-black/90 backdrop-blur flex items-center justify-center p-4" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} onClick={() => setIndex(null)} data-testid="gallery-lightbox">
      <button className="absolute top-5 right-5 text-white" onClick={() => setIndex(null)} aria-label="Close" data-testid="gallery-lightbox-close"><X size={30} /></button>
      <button className="absolute left-3 sm:left-8 text-white p-3" onClick={(e) => { e.stopPropagation(); go(-1); }} aria-label="Previous" data-testid="gallery-prev"><ChevronLeft size={36} /></button>
      <motion.img key={item.src} src={imgUrl(item.src)} alt={item.alt} initial={{ scale: 0.9, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} className="max-h-[88vh] max-w-[90vw] rounded-2xl object-contain" onClick={(e) => e.stopPropagation()} />
      <button className="absolute right-3 sm:right-8 text-white p-3" onClick={(e) => { e.stopPropagation(); go(1); }} aria-label="Next" data-testid="gallery-next"><ChevronRight size={36} /></button>
    </motion.div>
  );
};

export default function Gallery() {
  const { tr } = useLang();
  const [extra, setExtra] = useState([]);
  const [index, setIndex] = useState(null);
  usePageMeta(tr("Photo Gallery – SunCare Dental Nicaragua", "Galería de Fotos – SunCare Dental Nicaragua"), tr("Photos of Dra. Cora Baltodano, our team and our dental clinics in San Juan del Sur and Managua, Nicaragua.", "Fotos de la Dra. Cora Baltodano, nuestro equipo y nuestras clínicas dentales en San Juan del Sur y Managua, Nicaragua."), undefined, "/img/cora-team.jpg");
  useEffect(() => {
    axios.get(`${API}/gallery`).then((r) => setExtra(r.data.map((g) => ({ src: g.url, alt: g.caption || "SunCare Dental" })))).catch(() => {});
  }, []);
  const items = [...extra, ...GALLERY];
  return (
    <>
      <PageHero eyebrow={tr("Gallery", "Galería")} title={tr(<>Smiles, team & <span className="text-[#00C9B7]">our clinics</span></>, <>Sonrisas, equipo y <span className="text-[#00C9B7]">nuestras clínicas</span></>)} img="/img/cora-team.jpg" />
      <section className="pb-24 sm:pb-32">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 columns-1 sm:columns-2 lg:columns-3 gap-5 [&>*]:mb-5" data-testid="gallery-grid">
          {items.map((g, i) => (
            <motion.button key={g.src} onClick={() => setIndex(i)} initial={{ opacity: 0, y: 60, clipPath: "inset(45% 0% 45% 0% round 24px)" }} whileInView={{ opacity: 1, y: 0, clipPath: "inset(0% 0% 0% 0% round 24px)" }} viewport={{ once: false, amount: 0.01 }} transition={{ duration: 0.8, delay: (i % 3) * 0.1, ease: [0.16, 1, 0.3, 1] }} className="group block w-full break-inside-avoid overflow-hidden rounded-3xl border border-[#00C9B7]/15" data-testid={`gallery-item-${i}`}>
              <img src={imgUrl(g.src)} alt={g.alt} loading="lazy" className="w-full h-auto group-hover:scale-105 transition-transform duration-700" />
            </motion.button>
          ))}
        </div>
      </section>
      <AnimatePresence>{index !== null && <Lightbox items={items} index={index} setIndex={setIndex} />}</AnimatePresence>
    </>
  );
}
